/**
 * Simple reader of an ASCII DXF for the plan viewer (ported from the prototype): lines, circles,
 * arcs, polylines (with bulges), ellipses and splines become SVG path data, with the overall size.
 * Blocks, texts and dimensions are not drawn — the file stays downloadable for the CAD software.
 */

export interface DxfPreview {
  paths: string[];
  viewBox: string;
  /** « Encombrement : 1 250 × 600 mm · 42 élément(s) dessiné(s) » */
  dim: string;
}

const UNITS: Record<string, string> = { '1': 'pouces', '4': 'mm', '5': 'cm', '6': 'm' };

/** A binary DXF starts with this header: it can't be read here. */
export const isBinaryDxf = (head: Uint8Array) =>
  new TextDecoder('latin1').decode(head.slice(0, 22)).startsWith('AutoCAD Binary DXF');

const fmt = (x: number) => x.toLocaleString('fr-FR', { maximumFractionDigits: 2 });

export function dxfPreview(txt: string): DxfPreview | null {
  const ln = String(txt).split(/\r\n|\r|\n/);
  const P: [number, string][] = [];
  for (let i = 0; i + 1 < ln.length; i += 2) P.push([parseInt(ln[i], 10), ln[i + 1].trim()]);
  const iu = P.findIndex((p) => p[0] === 9 && p[1] === '$INSUNITS');
  const units = iu >= 0 && P[iu + 1] ? UNITS[P[iu + 1][1]] || '' : '';
  let i = P.findIndex((p, k) => p[0] === 2 && p[1] === 'ENTITIES' && k > 0 && P[k - 1][1] === 'SECTION');
  if (i < 0) return null;

  type Ent = { t: string; d: [number, string][] };
  const ents: Ent[] = [];
  let cur: Ent | null = null;
  for (i = i + 1; i < P.length; i++) {
    const [c, v] = P[i];
    if (c === 0) {
      if (cur) ents.push(cur);
      cur = null;
      if (v === 'ENDSEC') break;
      cur = { t: v, d: [] };
    } else if (cur) cur.d.push([c, v]);
  }
  if (cur) ents.push(cur);

  const n = (x: number) => +(+x).toFixed(3);
  const ok = (...a: number[]) => a.every((x) => Number.isFinite(x));
  const paths: string[] = [];
  let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity;
  const add = (x: number, y: number) => {
    if (ok(x, y)) { if (x < x0) x0 = x; if (x > x1) x1 = x; if (y < y0) y0 = y; if (y > y1) y1 = y; }
  };
  const gv = (e: Ent, c: number) => { const p = e.d.find((x) => x[0] === c); return p ? parseFloat(p[1]) : NaN; };
  const flag = (e: Ent) => parseInt((e.d.find((x) => x[0] === 70) || [0, '0'])[1], 10) || 0;

  type V = { x: number; y: number; b: number };
  const verts = (vs: V[], closed: boolean) => {
    vs = vs.filter((p) => ok(p.x, p.y));
    if (vs.length < 2) return;
    let d = `M${n(vs[0].x)} ${n(vs[0].y)}`;
    const cnt = closed ? vs.length : vs.length - 1;
    for (let k = 0; k < cnt; k++) {
      const a = vs[k], b = vs[(k + 1) % vs.length], bu = a.b || 0;
      add(a.x, a.y); add(b.x, b.y);
      if (bu) {
        const ch = Math.hypot(b.x - a.x, b.y - a.y);
        if (!ch) continue;
        const r = (ch * (1 + bu * bu)) / (4 * Math.abs(bu));
        add((a.x + b.x) / 2 + (bu / 2) * (b.y - a.y), (a.y + b.y) / 2 - (bu / 2) * (b.x - a.x));
        d += `A${n(r)} ${n(r)} 0 ${Math.abs(bu) > 1 ? 1 : 0} ${bu > 0 ? 1 : 0} ${n(b.x)} ${n(b.y)}`;
      } else d += `L${n(b.x)} ${n(b.y)}`;
    }
    if (closed) d += 'Z';
    paths.push(d);
  };

  let poly: { vs: V[]; closed: boolean } | null = null;
  const R = Math.PI / 180;
  for (const e of ents) {
    if (e.t === 'LINE') {
      const a = gv(e, 10), b = gv(e, 20), c = gv(e, 11), d = gv(e, 21);
      if (ok(a, b, c, d)) { add(a, b); add(c, d); paths.push(`M${n(a)} ${n(b)}L${n(c)} ${n(d)}`); }
    } else if (e.t === 'CIRCLE') {
      const x = gv(e, 10), y = gv(e, 20), r = gv(e, 40);
      if (ok(x, y, r) && r > 0) {
        add(x - r, y - r); add(x + r, y + r);
        paths.push(`M${n(x - r)} ${n(y)}A${n(r)} ${n(r)} 0 1 0 ${n(x + r)} ${n(y)}A${n(r)} ${n(r)} 0 1 0 ${n(x - r)} ${n(y)}`);
      }
    } else if (e.t === 'ARC') {
      const x = gv(e, 10), y = gv(e, 20), r = gv(e, 40), a1 = gv(e, 50), a2 = gv(e, 51);
      if (ok(x, y, r, a1, a2) && r > 0) {
        let sw = (a2 - a1) % 360;
        if (sw <= 0) sw += 360;
        for (let k = 0; k <= 16; k++) { const t = (a1 + (sw * k) / 16) * R; add(x + r * Math.cos(t), y + r * Math.sin(t)); }
        paths.push(`M${n(x + r * Math.cos(a1 * R))} ${n(y + r * Math.sin(a1 * R))}A${n(r)} ${n(r)} 0 ${sw > 180 ? 1 : 0} 1 ${n(x + r * Math.cos(a2 * R))} ${n(y + r * Math.sin(a2 * R))}`);
      }
    } else if (e.t === 'LWPOLYLINE') {
      const vs: V[] = [];
      let lv: V | null = null;
      for (const [c, v] of e.d) {
        if (c === 10) { lv = { x: parseFloat(v), y: NaN, b: 0 }; vs.push(lv); }
        else if (c === 20 && lv) lv.y = parseFloat(v);
        else if (c === 42 && lv) lv.b = parseFloat(v) || 0;
      }
      verts(vs, (flag(e) & 1) === 1);
    } else if (e.t === 'POLYLINE') poly = { vs: [], closed: (flag(e) & 1) === 1 };
    else if (e.t === 'VERTEX' && poly) poly.vs.push({ x: gv(e, 10), y: gv(e, 20), b: gv(e, 42) || 0 });
    else if (e.t === 'SEQEND' && poly) { verts(poly.vs, poly.closed); poly = null; }
    else if (e.t === 'ELLIPSE') {
      const cx = gv(e, 10), cy = gv(e, 20), mx = gv(e, 11), my = gv(e, 21), ra = gv(e, 40);
      let t1 = gv(e, 41), t2 = gv(e, 42);
      if (!ok(t1)) t1 = 0;
      if (!ok(t2)) t2 = 2 * Math.PI;
      if (ok(cx, cy, mx, my, ra)) {
        let sw = t2 - t1;
        if (sw <= 0) sw += 2 * Math.PI;
        const a = Math.hypot(mx, my), b = a * ra, rot = Math.atan2(my, mx);
        let d = '';
        for (let k = 0; k <= 48; k++) {
          const t = t1 + (sw * k) / 48;
          const x = cx + a * Math.cos(t) * Math.cos(rot) - b * Math.sin(t) * Math.sin(rot);
          const y = cy + a * Math.cos(t) * Math.sin(rot) + b * Math.sin(t) * Math.cos(rot);
          add(x, y);
          d += (k ? 'L' : 'M') + n(x) + ' ' + n(y);
        }
        paths.push(d);
      }
    } else if (e.t === 'SPLINE') {
      const rd = (cx: number, cy: number) => {
        const q: { x: number; y: number }[] = [];
        let l: { x: number; y: number } | null = null;
        for (const [c, v] of e.d) {
          if (c === cx) { l = { x: parseFloat(v), y: NaN }; q.push(l); }
          else if (c === cy && l) l.y = parseFloat(v);
        }
        return q.filter((p) => ok(p.x, p.y));
      };
      const fp = rd(11, 21);
      const q = fp.length > 1 ? fp : rd(10, 20);
      if (q.length > 1) {
        q.forEach((p) => add(p.x, p.y));
        paths.push(q.map((p, k) => (k ? 'L' : 'M') + n(p.x) + ' ' + n(p.y)).join(''));
      }
    }
  }
  if (!paths.length || !ok(x0, y0, x1, y1)) return null;
  const w = x1 - x0, h = y1 - y0, pad = Math.max(w, h, 1) * 0.04;
  return {
    paths,
    // y goes up in a DXF and down in SVG: the paths are drawn inside scale(1,-1)
    viewBox: `${n(x0 - pad)} ${n(-y1 - pad)} ${n(w + 2 * pad)} ${n(h + 2 * pad)}`,
    dim: `Encombrement : ${fmt(w)} × ${fmt(h)} ${units || '(unités du fichier)'} · ${paths.length} élément(s) dessiné(s)`,
  };
}
